import { store } from '@/store';
import { readPersistedAuthSlice } from '@/utils/auth/readPersistedAuthSlice';

/**
 * Read the persisted Auth0-desktop access token — Redux first (live
 * session), falling back to the redux-persist storage layer directly
 * for cold-relaunch scenarios where the rematch models haven't fully
 * rehydrated yet.
 *
 * Consumed by:
 * - `DesktopAuthProvider` (`provider.tsx`) — offline session restore
 *   at provider-init time, writes Redux + provider state.
 * - `readAuth0Desktop` (in `src/lib/auth/readFromProviderSourceOfTruth.ts`)
 *   — offline fallback in the cache branch, writes the cache atoms so
 *   `useIsAuthenticated()` consumers unblock.
 *
 * Extracted to a plain module (no React deps) so the cache-layer
 * consumer doesn't pull the React provider tree into
 * `readFromProviderSourceOfTruth.ts`.
 *
 * **Persistence dependency:** reads `state.auth.token` and the
 * redux-persist `auth` slice. Both are on the eventual dual-run
 * removal path. Before those are deleted, the offline-restore source
 * needs to migrate to a survivable storage key (e.g. a dedicated
 * key written on session events, or a no-refresh stored-token read
 * from the Rust plugin) — otherwise offline cold relaunch AND the
 * cache's offline fallback both silently break. Coordinate with the
 * offline-recording feature owner.
 */
export function getPersistedAuth0DesktopToken(): string | undefined {
  const reduxToken = store.getState().auth?.token;
  if (reduxToken) return reduxToken;

  return readPersistedAuthSlice<{ token?: string }>()?.token;
}
